// Library Imports
import Cookies from "js-cookie";
import { toast } from 'react-toastify';

// Local Imports
import { persistor, store } from '@/redux/store';
import { logout } from '@/redux/features/CurrentUser/currentUserSlice';
import { profilePic } from '@/assets/images';
import { resetBlurKit, resetMediaKitState } from '@/redux/features/MediaKit/mediakitSlice';
import endPoints from '@/redux/constant';
import { deliverables, ratesSocialChannel, allowedPlatforms } from '@/utils/staticData';
import { INSTAGRAM_FACT_NAME_HIGHEST_ENGAGEMENT } from '@/components/MediaKit/FavouriteFacts/favouriteFactsDisplay';
import { trackUserEvent, USER_EVENTS } from '@/utils/trackUserEvent';

const activeToasts = new Set();

export const MK_PREVIEW_MODE_KEY = 'mkPreviewMode';

export const setMediaKitPreviewMode = () => {
  try {
    sessionStorage.setItem(MK_PREVIEW_MODE_KEY, 'true');
  } catch {
    // sessionStorage unavailable (private mode, etc.) — preview mode just won't be flagged.
  }
};

export const clearMediaKitPreviewMode = () => {
  try {
    sessionStorage.removeItem(MK_PREVIEW_MODE_KEY);
  } catch {
    // sessionStorage unavailable — nothing to clear.
  }
};

export const isMediaKitPreviewMode = () => {
  try {
    return sessionStorage.getItem(MK_PREVIEW_MODE_KEY) === 'true';
  } catch {
    return false;
  }
};

/** Normalize API / thunk error payloads into a user-facing string. */
export const getApiErrorMessage = (error, fallback) => {
  if (error == null || error === '') return fallback;

  if (typeof error === 'string') {
    const trimmed = error.trim();
    if (!trimmed) return fallback;
    if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
      try {
        return getApiErrorMessage(JSON.parse(trimmed), trimmed);
      } catch {
        return trimmed;
      }
    }
    return trimmed;
  }

  if (typeof error !== 'object') return String(error);

  const tiktokMessage = error?.error?.message;
  if (typeof tiktokMessage === 'string' && tiktokMessage.trim()) {
    return tiktokMessage.trim();
  }

  if (typeof error?.message === 'string' && error.message.trim()) {
    return error.message.trim();
  }

  const exceptionMessage = error?.responseException?.exceptionMessage;
  if (exceptionMessage != null && exceptionMessage !== '') {
    return getApiErrorMessage(exceptionMessage, fallback);
  }

  if (typeof error?.title === 'string' && error.title.trim()) {
    return error.title.trim();
  }

  const validationErrors = error?.errors;
  if (validationErrors && typeof validationErrors === 'object') {
    const firstField = Object.values(validationErrors).find(
      value => Array.isArray(value) && typeof value[0] === 'string' && value[0].trim()
    );
    if (firstField?.[0]) return firstField[0].trim();
  }

  return fallback;
};

const createToast = (type) => (message, options = {}) => {
  const normalizedMessage = getApiErrorMessage(
    message,
    typeof message === 'string' ? message.trim() : undefined
  );

  if (!normalizedMessage) return;

  if (activeToasts?.has(normalizedMessage)) return;

  const id = toast[type](normalizedMessage, {
    ...options,
    onClose: () => activeToasts?.delete(normalizedMessage),
  });

  activeToasts.add(normalizedMessage);
  return id;
};

// =========================== Toast =======================
export const customToast = {
  info: createToast('info'),
  success: createToast('success'),
  error: createToast('error'),
  warn: createToast('warn'),
  warning: createToast('warning'),
  default: createToast(''),
};

// ==================== Auth Utilities ====================
export const resetAppStore = async () => {
  await persistor.pause();
  await persistor.flush();
  await persistor.purge();
  localStorage.removeItem('persist:root');
  sessionStorage.clear();
  store.dispatch({ type: 'RESET_APP' });
  persistor.persist();
};

// ====================== Logout Utilities ==========================
export const logoutFn = async () => {
  Cookies.remove("_wp_knock", { path: "/" });
  const dispatch = store.dispatch;

  const userId = store.getState()?.currentUser?.currentUser?.userId;
  trackUserEvent(USER_EVENTS.LOGOUT, userId);
  dispatch(resetMediaKitState());
  dispatch(logout());

  await resetAppStore();

  await new Promise(res => setTimeout(res, 50));

  try {
    const res = await fetch(endPoints.logoutWp, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "sso-system-token": "ctIEBiAbOfO[qmD$2@WlK1w|ED1+AEpCh*71Re=k@w-&XNVg5",
      },
      credentials: "include",
    });

    await res.json();
  } catch (err) {
    console.log("err:", err)
  }
};

const WP_SSO_SYSTEM_TOKEN = 'ctIEBiAbOfO[qmD$2@WlK1w|ED1+AEpCh*71Re=k@w-&XNVg5';

/** Fire-and-forget WordPress SSO validate; does not block UI or surface errors. */
export const syncWpSessionInBackground = (email, password) => {
  const trimmedEmail = email?.trim();
  const trimmedPassword = password?.trim();
  if (!trimmedEmail || !trimmedPassword) return;

  void fetch(endPoints.loginWp, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'sso-system-token': WP_SSO_SYSTEM_TOKEN,
    },
    credentials: 'include',
    body: JSON.stringify({
      email: trimmedEmail,
      password: trimmedPassword,
    }),
  }).catch(() => { });
};

// ================== Clear Blur Kit Utilities =========================
export const clearBlurKit = () => {
  const dispatch = store.dispatch;
  dispatch(resetBlurKit());
};

// ==================== Password Utilities ====================
export const generateRandomPassword = (length = 8) => {
  const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const lowercase = 'abcdefghijklmnopqrstuvwxyz';
  const numbers = '0123456789';
  const specialChars = '!@#$%^&*()-_=+[]{}|;:,.<>?';

  const allChars = uppercase + lowercase + numbers + specialChars;
  let password = '';

  // Ensure at least one character from each category
  password += uppercase[Math.floor(Math.random() * uppercase?.length)];
  password += lowercase[Math.floor(Math.random() * lowercase?.length)];
  password += numbers[Math.floor(Math.random() * numbers?.length)];
  password += specialChars[Math.floor(Math.random() * specialChars?.length)];

  // Fill the rest of the password length
  for (let i = password?.length; i < length; i++) {
    password += allChars[Math.floor(Math.random() * allChars?.length)];
  }

  // Shuffle password to avoid predictable patterns
  return password
    ?.split('')
    ?.sort(() => Math.random() - 0.5)
    ?.join('');
};

// ==================== String Utilities ====================
export const truncateName = (text, maxLength, type) => {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  const start = text.slice(0, Math.ceil(maxLength / 2));
  const end = text.slice(-Math.floor(maxLength / 2));
  if (type === 'start') {
    return `${start}...`;
  } else {
    return `${start}...${end}`;
  }
};

// ==================== Capitalize First Letter Utilities ====================
export const capitalizeFirstLetter = word => {
  const wordsArray = word?.split(' ');

  if (wordsArray?.length === 1) {
    const trimmed = word?.trim()?.toLowerCase();
    return trimmed?.charAt(0)?.toUpperCase() + trimmed?.slice(1)?.toLowerCase();
  }

  const updatedWord = wordsArray?.map(item => {
    if (!item?.trim()) return '';
    const trimmed = item?.trim()?.toLowerCase();
    return trimmed?.charAt(0)?.toUpperCase() + trimmed?.slice(1)?.toLowerCase();
  });

  return updatedWord?.join(' ');
};

// ==================== Capitalize Words Utilities ====================
export const capitalizeWords = str => {
  if (!str) return '';
  return str
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

// ==================== Truncate Name Utilities ====================
export const trimName = string => {
  const startString = string?.slice(0, 10);
  const endString = string?.slice(string?.length - 6, string?.length);
  return `${startString}${endString}` || '';
};

// ==================== Number Utilities ====================
export const formatNumber = (number, decimals = 1, showDecimal = true) => {
  if (typeof number === 'string' && number?.includes('-')) {
    return number;
  }
  const num = Number(number);
  if (isNaN(num)) return;

  // Helper to ensure output is at most 3 chars (not counting suffix)
  const format = (divisor, suffix) => {
    let value = num / divisor;
    let output = value.toFixed(decimals);

    if (!showDecimal) {
      output = Math.round(value).toString();
    } else {
      // Try to keep as many decimals as possible without exceeding 3 chars
      for (let d = decimals; d >= 0; d--) {
        let test = value.toFixed(d);
        if (test.length <= 3) {
          output = test;
          break;
        }
      }
    }

    // If it's still too long, just take the first three chars (rounded up if >3 chars)
    if (output.length > 3) {
      output = Math.round(value).toString();
      if (output.length > 3) {
        // E.g. 9997 => 10k
        output = output.slice(0, 2); // Take first 2 digits to keep 3 chars with suffix
        output = Math.round(Number(output));
      }
      output = output.toString();
      output = output.slice(0, 3); // Ensure max 3 chars
    }

    return output + suffix;
  };

  if (num === 0) return '0';
  if (num < 1_000) {
    // Specifically: do NOT round when value is less than 1000, preserve decimals if showDecimal=true
    let v = showDecimal
      ? num.toFixed(decimals)
      : num.toString();
    // Remove unnecessary .0 for int values when showDecimal=false or the value is an integer
    if (!showDecimal || Number(v) % 1 === 0) {
      v = Number(num).toString();
    }
    if (v.length > 3) {
      // e.g. 999.6 => 1000 => show as 1k
      return format(1_000, 'k');
    }
    return v;
  }
  if (num < 1_000_000) return format(1_000, 'k');
  if (num < 1_000_000_000) return format(1_000_000, 'M');
  return format(1_000_000_000, 'B');
};

// ==================== Display Utilities ====================
export const formatAdminDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const displayValue = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  return String(value);
};

// ==================== URL Utilities ====================
export const getBaseUrl = () => {
  const protocol = window.location.protocol;
  const hostname = window.location.hostname;
  const port = window.location.port;

  return `${protocol}//${hostname}${port ? `:${port}` : ''}`;
};

// ==================== Verify Social In URL Utilities ====================
export const verifySocialInUrl = async value => {
  const lowerCaseValue = value?.toLowerCase();
  const matchedPlatform = allowedPlatforms?.find(platform => lowerCaseValue?.includes(platform));
  return matchedPlatform === 'youtu.be' ? 'youtube' : matchedPlatform || '';
};

/** YouTube watch / share URLs cannot be framed; returns embed URL for iframes, or null if not YouTube. */
export const toYouTubeEmbedUrl = rawUrl => {
  if (!rawUrl || typeof rawUrl !== 'string') return null;
  let s = rawUrl.trim();
  if (!s) return null;

  const iframeSrcMatch = s.match(/<iframe[^>]*\ssrc=["']?([^"'\s>]+)["']?/i);
  if (iframeSrcMatch?.[1]) {
    s = iframeSrcMatch[1].trim();
  }

  if (!/^https?:\/\//i.test(s)) {
    s = `https://${s}`;
  }
  let url;
  try {
    url = new URL(s);
  } catch {
    return null;
  }

  const host = url.hostname.replace(/^www\./i, '').toLowerCase();
  const youtubeHosts = new Set([
    'youtube.com',
    'm.youtube.com',
    'music.youtube.com',
    'youtu.be',
    'youtube-nocookie.com'
  ]);
  if (!youtubeHosts.has(host)) return null;

  let videoId = null;

  if (host === 'youtu.be') {
    videoId = url.pathname.split('/').filter(Boolean)[0];
  } else {
    const path = url.pathname;
    const fromPath =
      path.match(/\/embed\/([^/?]+)/) ||
      path.match(/\/shorts\/([^/?]+)/) ||
      path.match(/\/live\/([^/?]+)/) ||
      path.match(/\/v\/([^/?]+)/);
    if (fromPath) {
      videoId = fromPath[1];
    } else if (path === '/watch' || path.startsWith('/watch')) {
      videoId = url.searchParams.get('v');
    }
  }

  if (!videoId) return null;
  const cleanId = videoId.split(/[?&]/)[0];
  if (!/^[\w-]{6,}$/.test(cleanId)) return null;

  return `https://www.youtube.com/embed/${cleanId}`;
};

// ==================== Array Utilities ====================
export const chunkArray = (array, size) => {
  const result = [];
  for (let i = 0; i < array?.length; i += size) {
    result?.push(array?.slice(i, i + size));
  }
  return result;
};

// ==================== Misc Utilities ====================
export const getPathname = () => window.location.pathname;


// ==================== Font Size Utilities ====================
export const fontSizeClasses = (text) => {
  const length = text?.length || 0;

  if (length <= 9) {
    return 'text-[32px]';
  } else if (length <= 12) {
    return 'text-[28px]';
  } else if (length <= 15) {
    return 'text-[25px]';
  } else if (length <= 18) {
    return 'text-2xl';
  } else if (length <= 21) {
    return 'text-xl';
  } else if (length <= 24) {
    return 'text-lg';
  } else if (length <= 27) {
    return 'text-base';
  } else {
    return 'text-[16px]';
  }
}

// ==================== Upload File Type Utilities ====================
export const ALLOWED_UPLOAD_EXTENSIONS = [
  '.jpg',
  '.jpeg',
  '.png',
  '.gif',
  '.webp',
  '.pdf',
  '.heic',
  '.avif',
  '.jfif',
];

export const ALLOWED_UPLOAD_MIME_TYPES = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'application/pdf',
  'image/heic',
  'image/heif',
  'image/avif',
];

export const ALLOWED_UPLOAD_ACCEPT = ALLOWED_UPLOAD_EXTENSIONS.join(',');

export const ALLOWED_UPLOAD_ERROR_MESSAGE =
  'Allowed file types: JPG, JPEG, PNG, GIF, WEBP, PDF, HEIC, AVIF, JFIF.';

export const isAllowedUploadFile = file => {
  if (!file?.name) return false;

  const extension = `.${file.name.split('.').pop()?.toLowerCase()}`;
  const mimeType = file.type?.toLowerCase();

  return (
    ALLOWED_UPLOAD_EXTENSIONS.includes(extension) ||
    (mimeType && ALLOWED_UPLOAD_MIME_TYPES.includes(mimeType))
  );
};

// ==================== Image Utilities ====================
export const processImage = (file, canvasRef) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = e => {
      const img = new Image();
      img.src = e.target.result;

      img.onload = () => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');

        // Set canvas dimensions to the image dimensions
        canvas.width = img.width;
        canvas.height = img.height;

        // Draw the uploaded image
        ctx.drawImage(img, 0, 0);

        // Get the image data
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;

        const samplePoints = [
          { x: 0, y: 0 }, // Top-left corner
          { x: canvas.width - 1, y: 0 }, // Top-right corner
          { x: 0, y: canvas.height - 1 }, // Bottom-left corner
          { x: canvas.width - 1, y: canvas.height - 1 } // Bottom-right corner
        ];

        const bgColors = samplePoints.map(point => {
          const index = (point.y * canvas.width + point.x) * 4;
          return {
            r: data[index],
            g: data[index + 1],
            b: data[index + 2]
          };
        });

        // Average the background color
        const bgColor = {
          r: bgColors.reduce((sum, color) => sum + color.r, 0) / bgColors.length,
          g: bgColors.reduce((sum, color) => sum + color.g, 0) / bgColors.length,
          b: bgColors.reduce((sum, color) => sum + color.b, 0) / bgColors.length
        };

        // Remove background (assume light backgrounds for simplicity)
        const tolerance = 60; // Adjust this for better results
        for (let i = 0; i < data.length; i += 4) {
          const red = data[i];
          const green = data[i + 1];
          const blue = data[i + 2];
          // const alpha = data[i + 3];

          // Check for light colors (background)
          if (
            Math.abs(red - bgColor.r) < tolerance &&
            Math.abs(green - bgColor.g) < tolerance &&
            Math.abs(blue - bgColor.b) < tolerance
            // alpha > 50
          ) {
            // Make pixel transparent
            data[i + 3] = 0;
          } else {
            // Convert non-background pixels to black
            data[i] = 0; // Red
            data[i + 1] = 0; // Green
            data[i + 2] = 0; // Blue
          }
        }

        // Put modified data back
        ctx.putImageData(imageData, 0, 0);

        // Convert the canvas content to a Blob
        canvas.toBlob(blob => {
          // Create a new File object from the Blob
          const processedFile = new File([blob], file.name, {
            type: 'image/png'
          });
          resolve(processedFile);
        }, 'image/png');
      };
    };
    reader.onerror = error => reject(error);
    reader.readAsDataURL(file);
  });
};

// ==================== Media Kit Functions ====================
const prepareTopPosts = (instagramData, data) => {
  const posts = instagramData?.postData || [];
  const result = posts?.map((post, index) => ({
    socialChannelName: 'Instagram',
    freePost: JSON.stringify({
      socialChannel: 'Instagram',
      link: post?.permalink,
      likesValue: post?.like_count,
      commentsValue: post?.comments_count,
      mediaUrl: post?.media_url,
      mediaType: post?.media_type,
      thumbnail_url: post?.thumbnail_url,
      likesInclude: null,
      engagementRatioValue: null,
      engagementRatioInclude: null,
      image: null,
      title: null,
      subtitle: null,
      caption: null,
      views: null,
      engagementRatio: null,
      pinsSaved: null,
      viewsInclude: null,
      subtitleInclude: null,
      subscribers: null,
      subscriberInclude: null
    }),
    paidPost: JSON.stringify({
      socialChannel: 'Instagram',
      link: post?.permalink,
      likesValue: post?.like_count,
      commentsValue: post?.comments_count,
      mediaUrl: post?.media_url,
      mediaType: post?.media_type,
      thumbnail_url: post?.thumbnail_url,
      likesInclude: null,
      engagementRatioValue: null,
      engagementRatioInclude: null,
      image: null,
      title: null,
      subtitle: null,
      caption: null,
      views: null,
      engagementRatio: null,
      pinsSaved: null,
      viewsInclude: null,
      subtitleInclude: null,
      subscribers: null,
      subscriberInclude: null
    }),
    postIndex: index
  }));

  // Fill the remaining slots with dummy data
  while (result?.length < 5) {
    const Index = result?.length;
    result.push({
      socialChannelName: 'File',
      freePost: JSON.stringify({
        socialChannel: 'File',
        link: null,
        likesValue: 0,
        commentsValue: 0,
        mediaUrl: data[Index],
        mediaType: 'IMAGE',
        thumbnail_url: null,
        likesInclude: null,
        engagementRatioValue: null,
        engagementRatioInclude: null,
        image: null,
        title: null,
        subtitle: null,
        caption: null,
        views: null,
        engagementRatio: null,
        pinsSaved: null,
        viewsInclude: null,
        subtitleInclude: null,
        subscribers: null,
        subscriberInclude: null
      }),
      paidPost: JSON.stringify({
        socialChannel: 'File',
        link: null,
        likesValue: 0,
        commentsValue: 0,
        mediaUrl: data[Index],
        mediaType: 'IMAGE',
        thumbnail_url: null,
        likesInclude: null,
        engagementRatioValue: null,
        engagementRatioInclude: null,
        image: null,
        title: null,
        subtitle: null,
        caption: null,
        views: null,
        engagementRatio: null,
        pinsSaved: null,
        viewsInclude: null,
        subtitleInclude: null,
        subscribers: null,
        subscriberInclude: null
      })
    });
  }

  return result;
};

// Prepare Favorites Fact Data
const prepareFavFact = instagramData => {
  const followersCount = instagramData?.followers_count || 0;
  const highestEngagement = instagramData?.highestEngagement || 0;
  const mediaCount = instagramData?.media_count || 0;
  const currentDateTime = new Date().toISOString();

  const favFactsArray = [
    {
      socialChannelName: 'Instagram',
      factName: 'Followers',
      factValue: followersCount?.toString(),
      isPaid: false,
      lastUpdatedDateTime_dtm: currentDateTime,
      lastUpdatedByUserID_chr: null,
      isDeleted_ysn: false,
      row_Version_Binary_bin: null
    },
    {
      socialChannelName: 'Instagram',
      factName: INSTAGRAM_FACT_NAME_HIGHEST_ENGAGEMENT,
      factValue: highestEngagement?.toString(),
      isPaid: true,
      lastUpdatedDateTime_dtm: currentDateTime,
      lastUpdatedByUserID_chr: null,
      isDeleted_ysn: false,
      row_Version_Binary_bin: null
    },
    {
      socialChannelName: 'Instagram',
      factName: 'NO. of posts',
      factValue: mediaCount?.toString(),
      isPaid: false,
      lastUpdatedDateTime_dtm: currentDateTime,
      lastUpdatedByUserID_chr: null,
      isDeleted_ysn: false,
      row_Version_Binary_bin: null
    }
  ];
  return favFactsArray;
};

const getAgeRange = age => {
  if (age == null || String(age).trim() === '') return null;
  const n = Number(age);
  if (isNaN(n)) return null;
  if (n < 18) return '<18';
  if (n < 25) return '18-24';
  if (n < 35) return '25-34';
  if (n < 45) return '35-44';
  if (n < 55) return '45-54';
  return '55+';
};

const getUrlParams = () => {
  const params = new URLSearchParams(window.location.search);
  const country = params.get('country') || null;
  const city = params.get('city') || null;
  const ageRange = getAgeRange(params.get('age'));
  const firstName = params.get('first_name') || null;
  const interests = ['interest_1', 'interest_2', 'interest_3']
    .map(key => params.get(key))
    .filter(Boolean);
  return { country, city, ageRange, firstName, interests };
};

export const prepareMediaKit = async (
  instagramData,
  createSampleKit,
  registeredUserInfo,
  topPosts,
  validInstagramAccount,
) => {
  const urlParams = getUrlParams();

  return {
    mediaKit: {
      ...(instagramData?.id && { instaUserId: instagramData?.id }),
      userId: registeredUserInfo?.userId,
      fullName: instagramData?.name || createSampleKit?.instagramUsername?.trim(),
      userName: createSampleKit?.instagramUsername?.trim(),
      firstName: urlParams.firstName || instagramData?.name?.split(' ')[0] || '',
      profileUrl: `http://instagram.com/` + createSampleKit?.instagramUsername?.trim(),
      profilePicUrl: instagramData?.profile_picture_url || profilePic,
      isProfileVideoPriority: false,
      bio: instagramData?.biography || '',
      ageRange: urlParams.ageRange || '25-34',
      topInterests: urlParams.interests.length > 0 ? urlParams.interests.join(',') : '',
      posts: instagramData?.media_count || 0,
      highestEngagement: instagramData?.highestEngagement || 0,
      highestEngagementPostUrl: instagramData?.postData?.length
        ? instagramData?.postData[0]?.permalink
        : '',
      followers: instagramData?.followers_count || 0,
      following: instagramData?.follows_count || 0,
      isFullNameUpdated: false,
      isBioUpdated: false,
      isProfilePicUpdated: false,
      isAgeRangeUpdated: false,
      isCountryCityUpdated: false,
      isSavedPassword: false,
      email: createSampleKit?.email,
      ...(urlParams.country && { country: urlParams.country }),
      ...(urlParams.city && { city: urlParams.city }),
      isInstaHandlePersonal: !validInstagramAccount,
      // True Sections
      isAgeRangeIncluded: true,
      isCountryIncluded: true,
      isDescriptionSectionToggle: true,
      isFavFactSectionToggle: true,
      isTopPostSectionToggle: true,
      isTopInterestSectionToggle: true,
      isBrandPartnerSectionToggle: true,
      isContentPost1SectionToggle: true,
      isDeliverableSectionToggle: true,
      isCtaBannerIncluded: true,
      isReviewSectionToggle: true,
      isSliderVideoSectionToggle: true,
      isRateSectionToggle: true,
      isContentPost2SectionToggle: true,
      isSalesLink: true,
      isEmailSectionToggle: true,
      // Remove this one when converting to free MK
      isPremiumMediaKit: true,
    },
    socialChannels: [
      {
        name: 'Instagram',
        userName: createSampleKit?.instagramUsername?.trim(),
        socialLink: `http://instagram.com/` + createSampleKit?.instagramUsername?.trim(),
        isToggleOn: true,
        engagementRate: null,
        engagement: instagramData?.highestEngagement || 0,
        followers: instagramData?.followers_count || 0,
        views: null,
        pinsSaved: null,
        posts: instagramData?.media_count || 0
      },
    ],
    topPosts: prepareTopPosts(instagramData, topPosts),
    deliverables: deliverables?.map(item => ({ deliverableName: item, isShow: true })),
    rates: ratesSocialChannel?.map(item => ({
      socialChannelName: item,
      minRate: '100',
      maxRate: '1000',
      isGifted: false,
      isIncluded: true
    })),
    collaborationBanner: {
      imageUrl:
        instagramData?.profile_picture_url ||
        'https://s3-alpha-sig.figma.com/img/6d2d/54bd/64fa62517290cb9c59d0e94f038731a1?Expires=1737936000&Key-Pair-Id=APKAQ4GOSFWCVNEHN3O4&Signature=ZUeskqQuyzBznKQoFfuT4byYejJ1BeyOqM6nd0nUtwO6D5dVf4MUNUTzZia1qLrg1i3a8cFQpzIrYtUdLmrj9TO~PeRU8ovQ7JRiptDV2IECq3a8IYptZ3T2da0vRqnUdBurpj0Suor7PHGb1xmUuwgjGE3ORvfrs7oyS-WI4PJL2Jjwd95XdXYKdywsAVXvu6X7DWA5y-Bk8UPPCxK7uIwYkK~08fCqOeV3l4Ol-FkW-f3ddB4re17IQlXF-419rayHsMpc5JTfHhKaxrHYMLxcm6XPCA7VHmIABQdjahwcnwMJ2TPzAqYVaF-4zbhQuNzujBxFeF9a03pObO1g5g__',
      bannerTitle:
        'Work with ' +
        (instagramData?.name?.split(' ')[0] || instagramData?.name || createSampleKit?.instagramUsername?.trim()),
      ctaLabel: 'Connect',
      contactInfo: createSampleKit?.email,
      isEmailContactInfo: true
    },
    favFacts: prepareFavFact(instagramData),
    favouriteContents: prepareFavouriteContents(instagramData?.favouriteContents, 'Instagram'),
  };
};

const prepareFavouriteContents = (items, socialChannelName = 'Tiktok') => {
  if (!Array.isArray(items)) return [];
  return items
    .map(item => ({
      socialChannelName: item?.socialChannelName || socialChannelName,
      videoUrl:
        item?.media_url ||
        item?.videoUrl ||
        item?.permalink ||
        item?.share_url ||
        item?.shareUrl ||
        item?.video_url ||
        '',
      thumbnailUrl:
        item?.thumbnail_url ||
        item?.thumbnailUrl ||
        item?.cover_image_url ||
        item?.coverImageUrl ||
        '',
    }))
    .filter(item => item.videoUrl || item.thumbnailUrl);
};

const normalizeTikTokSignupData = tiktokData => {
  if (!tiktokData || typeof tiktokData !== 'object') return {};
  const posts = tiktokData?.postData || tiktokData?.posts || tiktokData?.videos || [];
  const rawFavouriteContents =
    tiktokData?.favouriteContents ||
    [];
  return {
    ...tiktokData,
    name: tiktokData?.display_name || tiktokData?.name,
    username: tiktokData?.name || tiktokData?.userName,
    profile_picture_url:
      tiktokData?.profile_picture_url ||
      tiktokData?.profilePicUrl,
    biography: tiktokData?.biography || tiktokData?.bio || tiktokData?.signature,
    followers_count:
      tiktokData?.followers_count ?? tiktokData?.follower_count ?? tiktokData?.followerCount ?? 0,
    follows_count:
      tiktokData?.follows_count ?? tiktokData?.following ?? tiktokData?.followingCount ?? 0,
    media_count:
      tiktokData?.media_count ?? tiktokData?.video_count ?? tiktokData?.videoCount ?? 0,
    highestEngagement: tiktokData?.highestEngagement ?? tiktokData?.highest_engagement ?? 0,
    postData: posts,
    favouriteContents: prepareFavouriteContents(rawFavouriteContents, 'Tiktok'),
  };
};

const prepareTopPostsTikTok = (tiktokData, data) => {
  const posts = tiktokData?.postData || [];
  const result = posts?.map((post, index) => {
    const link =
      post?.permalink ||
      post?.share_url ||
      post?.shareUrl ||
      post?.video_url ||
      post?.videoUrl;
    const mediaUrl =
      post?.media_url || post?.cover_image_url || post?.coverImageUrl || post?.thumbnail_url;
    const postPayload = {
      socialChannel: 'Tiktok',
      link,
      likesValue: post?.like_count ?? post?.likes ?? post?.digg_count ?? 0,
      commentsValue: post?.comments_count ?? post?.comments ?? post?.comment_count ?? 0,
      mediaUrl,
      mediaType: post?.media_type || 'VIDEO',
      thumbnail_url: post?.thumbnail_url || mediaUrl,
      likesInclude: null,
      engagementRatioValue: null,
      engagementRatioInclude: null,
      image: null,
      title: null,
      subtitle: null,
      caption: null,
      views: post?.view_count ?? post?.views ?? post?.play_count ?? null,
      engagementRatio: null,
      pinsSaved: null,
      viewsInclude: null,
      subtitleInclude: null,
      subscribers: null,
      subscriberInclude: null,
    };
    return {
      socialChannelName: 'Tiktok',
      freePost: JSON.stringify(postPayload),
      paidPost: JSON.stringify(postPayload),
      postIndex: index,
    };
  });

  while (result?.length < 5) {
    const Index = result?.length;
    result.push({
      socialChannelName: 'File',
      freePost: JSON.stringify({
        socialChannel: 'File',
        link: null,
        likesValue: 0,
        commentsValue: 0,
        mediaUrl: data[Index],
        mediaType: 'IMAGE',
        thumbnail_url: null,
        likesInclude: null,
        engagementRatioValue: null,
        engagementRatioInclude: null,
        image: null,
        title: null,
        subtitle: null,
        caption: null,
        views: null,
        engagementRatio: null,
        pinsSaved: null,
        viewsInclude: null,
        subtitleInclude: null,
        subscribers: null,
        subscriberInclude: null,
      }),
      paidPost: JSON.stringify({
        socialChannel: 'File',
        link: null,
        likesValue: 0,
        commentsValue: 0,
        mediaUrl: data[Index],
        mediaType: 'IMAGE',
        thumbnail_url: null,
        likesInclude: null,
        engagementRatioValue: null,
        engagementRatioInclude: null,
        image: null,
        title: null,
        subtitle: null,
        caption: null,
        views: null,
        engagementRatio: null,
        pinsSaved: null,
        viewsInclude: null,
        subtitleInclude: null,
        subscribers: null,
        subscriberInclude: null,
      }),
    });
  }

  return result;
};

const prepareFavFactTikTok = tiktokData => {
  const followersCount = tiktokData?.followers_count || 0;
  const highestEngagement = tiktokData?.highestEngagement || 0;
  const mediaCount = tiktokData?.media_count || 0;
  const currentDateTime = new Date().toISOString();

  return [
    {
      socialChannelName: 'Tiktok',
      factName: 'Followers',
      factValue: followersCount?.toString(),
      isPaid: false,
      lastUpdatedDateTime_dtm: currentDateTime,
      lastUpdatedByUserID_chr: null,
      isDeleted_ysn: false,
      row_Version_Binary_bin: null,
    },
    {
      socialChannelName: 'Tiktok',
      factName: INSTAGRAM_FACT_NAME_HIGHEST_ENGAGEMENT,
      factValue: highestEngagement?.toString(),
      isPaid: true,
      lastUpdatedDateTime_dtm: currentDateTime,
      lastUpdatedByUserID_chr: null,
      isDeleted_ysn: false,
      row_Version_Binary_bin: null,
    },
    {
      socialChannelName: 'Tiktok',
      factName: 'NO. of posts',
      factValue: mediaCount?.toString(),
      isPaid: false,
      lastUpdatedDateTime_dtm: currentDateTime,
      lastUpdatedByUserID_chr: null,
      isDeleted_ysn: false,
      row_Version_Binary_bin: null,
    },
  ];
};

export const prepareMediaKitTikTok = async (
  tiktokData,
  createSampleKit,
  registeredUserInfo,
  topPosts,
  validTikTokAccount,
) => {
  const normalized = normalizeTikTokSignupData(tiktokData);
  const urlParams = getUrlParams();
  const displayName = normalized?.name;

  return {
    mediaKit: {
      ...(normalized?.id && { tiktokUserId: normalized?.id }),
      userId: registeredUserInfo?.userId,
      fullName: displayName,
      userName: normalized?.username || normalized?.name,
      firstName: urlParams.firstName || displayName?.split(' ')[0] || '',
      profileUrl: `https://www.tiktok.com/@${normalized?.username}`,
      profilePicUrl: normalized?.profile_picture_url || profilePic,
      isProfileVideoPriority: false,
      bio: normalized?.biography || '',
      ageRange: urlParams.ageRange || '25-34',
      topInterests: urlParams.interests.length > 0 ? urlParams.interests.join(',') : '',
      posts: normalized?.media_count || 0,
      highestEngagement: normalized?.highestEngagement || 0,
      highestEngagementPostUrl: normalized?.postData?.length
        ? normalized?.postData[0]?.permalink ||
        normalized?.postData[0]?.share_url ||
        normalized?.postData[0]?.shareUrl ||
        ''
        : '',
      followers: normalized?.followers_count || 0,
      following: normalized?.follows_count || 0,
      isFullNameUpdated: false,
      isBioUpdated: false,
      isProfilePicUpdated: false,
      isAgeRangeUpdated: false,
      isCountryCityUpdated: false,
      isSavedPassword: false,
      email: createSampleKit?.email,
      ...(urlParams.country && { country: urlParams.country }),
      ...(urlParams.city && { city: urlParams.city }),
      isInstaHandlePersonal: !validTikTokAccount,
      isAgeRangeIncluded: true,
      isCountryIncluded: true,
      isDescriptionSectionToggle: true,
      isFavFactSectionToggle: true,
      isTopPostSectionToggle: true,
      isTopInterestSectionToggle: true,
      isTiktokAuthenticated: true,
      isBrandPartnerSectionToggle: true,
      isContentPost1SectionToggle: true,
      isDeliverableSectionToggle: true,
      isCtaBannerIncluded: true,
      isReviewSectionToggle: true,
      isSliderVideoSectionToggle: true,
      isRateSectionToggle: true,
      isContentPost2SectionToggle: true,
      isSalesLink: true,
      isEmailSectionToggle: true,
      isPremiumMediaKit: true,
    },
    socialChannels: [
      {
        name: 'Tiktok',
        userName: normalized?.username || normalized?.name,
        socialLink: `https://www.tiktok.com/@${normalized?.username}`,
        isToggleOn: true,
        engagementRate: null,
        engagement: normalized?.highestEngagement || 0,
        followers: normalized?.followers_count || 0,
        views: null,
        pinsSaved: null,
        posts: normalized?.media_count || 0,
      },
    ],
    topPosts: prepareTopPostsTikTok(normalized, topPosts),
    deliverables: deliverables?.map(item => ({ deliverableName: item, isShow: true })),
    rates: ratesSocialChannel?.map(item => ({
      socialChannelName: item,
      minRate: '100',
      maxRate: '1000',
      isGifted: false,
      isIncluded: true,
    })),
    collaborationBanner: {
      imageUrl:
        normalized?.profile_picture_url ||
        'https://s3-alpha-sig.figma.com/img/6d2d/54bd/64fa62517290cb9c59d0e94f038731a1?Expires=1737936000&Key-Pair-Id=APKAQ4GOSFWCVNEHN3O4&Signature=ZUeskqQuyzBznKQoFfuT4byYejJ1BeyOqM6nd0nUtwO6D5dVf4MUNUTzZia1qLrg1i3a8cFQpzIrYtUdLmrj9TO~PeRU8ovQ7JRiptDV2IECq3a8IYptZ3T2da0vRqnUdBurpj0Suor7PHGb1xmUuwgjGE3ORvfrs7oyS-WI4PJL2Jjwd95XdXYKdywsAVXvu6X7DWA5y-Bk8UPPCxK7uIwYkK~08fCqOeV3l4Ol-FkW-f3ddB4re17IQlXF-419rayHsMpc5JTfHhKaxrHYMLxcm6XPCA7VHmIABQdjahwcnwMJ2TPzAqYVaF-4zbhQuNzujBxFeF9a03pObO1g5g__',
      bannerTitle: 'Work with ' + (displayName?.split(' ')[0] || displayName),
      ctaLabel: 'Connect',
      contactInfo: createSampleKit?.email,
      isEmailContactInfo: true,
    },
    favFacts: prepareFavFactTikTok(normalized),
    favouriteContents: normalized?.favouriteContents || [],
  };
};

export const liveMediaKitData = data => {
  const deliverables = JSON.parse(data?.mediaKitData?.deliverables_chr);
  const banner = JSON.parse(data?.mediaKitData?.collaborationBanner_chr);
  const rates = JSON.parse(data?.mediaKitData?.rates_chr);
  const partners = JSON.parse(data?.mediaKitData?.branParnetes_chr);
  const reviews = JSON.parse(data?.mediaKitData?.reviews_chr);
  const topVideos = JSON.parse(data?.mediaKitData?.favContente_chr);
  const links = JSON.parse(data?.mediaKitData?.salesLink_chr);

  return {
    mediaKit: data?.mediaKit,
    socialChannels: data?.mediaKitSocialChannel,
    topPosts: data?.mediaKitTopPost,
    deliverables: deliverables?.mediaKitDeliverables,
    rates: rates,
    collaborationBanner: banner,
    favFacts: data?.mediaKitFavFacts,
    brandPartners: partners?.mediaKitBrandPartners,
    brandReviews: reviews,
    favouriteContents: topVideos?.mediaKitFavouriteContents,
    salesLink: links
  };
};

/** Payload for POST /controlpanel/UpdateProfilePhoto (same intro URL merge as saveMediaKitDraftPayload mediaKit block). */
export const getUpdateProfilePhotoPayload = ({ mediaKitData, persistedMediaKitData, userId }) => {
  const profilePicUrl =
    mediaKitData?.intro?.introFile?.image || persistedMediaKitData?.mediaKit?.profilePicUrl || '';
  const profileVideoUrl =
    mediaKitData?.intro?.introFile?.video || persistedMediaKitData?.mediaKit?.profileVideoUrl || '';
  const profileVideoThumbnailUrl =
    mediaKitData?.intro?.introFile?.thumbNail ||
    persistedMediaKitData?.mediaKit?.profileVideoThumbnailUrl ||
    '';

  return {
    userId_chr: userId != null ? String(userId) : '',
    profilePicUrl_chr: profilePicUrl || '',
    profileVideoThumbnailUrl_chr: profileVideoThumbnailUrl || '',
    profileVideoUrl_chr: profileVideoUrl || ''
  };
};

// ==================== MEDIA KIT PAYLOAD FOR DRAFT ====================
export const saveMediaKitDraftPayload = params => {
  const mediaKitData = params?.mediaKitData;
  const mediaKitId = params?.mediaKitId;
  const persistedMediaKitData = params?.persistedMediaKitData;
  const currentUser = store.getState();

  // SOCIAL CHANNELS INTRO
  const socialChannelIntro = mediaKitData?.intro?.socialChannels
    ?.filter(
      item =>
      ((item?.socialLink || item?.data?.socialLink) !== null &&
        (item?.socialLink || item?.data?.socialLink) !== '')
    )
    .map(item => {
      return {
        mediaKitId: mediaKitId,
        name: item?.channelName || item?.name,
        userName: item?.channelUserName?.startsWith('@')
          ? item?.channelUserName?.slice(1)
          : item?.channelUserName || item?.userName,
        socialLink: item?.socialLink || item?.data?.socialLink,
        followers: item?.followers ?? item?.data?.followers,
        engagement: item?.engagement ?? item?.data?.engagement,
        posts: item?.posts ?? item?.data?.posts,
        likes: item?.likes ?? item?.data?.likes,
        engagementRate: item?.engagementRate,
        views: item?.views ?? item?.data?.views,
        totalLikes: item?.totalLikes ?? item?.data?.totalLikes,
        totalViews: item?.totalViews ?? item?.data?.totalViews,
        isToggleOn: item?.isToggleOn || item?.data?.isToggleOn ? true : false,
      };
    });

  const socialChannelPersistedIntro = persistedMediaKitData?.socialChannels?.map(item => {
    return {
      mediaKitId: mediaKitId,
      name: item?.name,
      isToggleOn: item?.isToggleOn,
      socialLink: item?.socialLink,
      userName: item?.userName,
      followers: item?.followers,
      engagementRate: item?.engagementRate,
      views: item?.views,
      posts: item?.posts,
      likes: item?.likes,
      engagement: item?.engagement,
      totalLikes: item?.totalLikes,
      totalViews: item?.totalViews
    };
  });

  return {
    mediaKit: {
      id: persistedMediaKitData?.mediaKit?.id,
      instaUserId: persistedMediaKitData?.mediaKit?.instaUserId,
      userId: persistedMediaKitData?.mediaKit?.userId,
      email: persistedMediaKitData?.mediaKit?.email,
      fullName: mediaKitData?.intro?.profileName || persistedMediaKitData?.mediaKit?.fullName,
      userName: persistedMediaKitData?.mediaKit?.userName,
      profileUrl: persistedMediaKitData?.mediaKit?.profileUrl,
      profilePicUrl:
        mediaKitData?.intro?.introFile?.image || persistedMediaKitData?.mediaKit?.profilePicUrl,
      profileVideoUrl: mediaKitData?.intro?.introFile?.video || persistedMediaKitData?.mediaKit?.profileVideoUrl,
      profileVideoSocial: ['instagram', 'youtube', 'youtu.be'].find(domain => mediaKitData?.intro?.introFile?.video?.includes(domain)) || persistedMediaKitData?.mediaKit?.profileVideoSocial,
      profileVideoThumbnailUrl: mediaKitData?.intro?.introFile?.thumbNail || persistedMediaKitData?.mediaKit?.profileVideoThumbnailUrl,
      isProfileVideoPriority: mediaKitData?.intro?.introFile?.video
        ? true
        : mediaKitData?.intro?.introFile?.image
          ? false
          : persistedMediaKitData?.mediaKit?.isProfileVideoPriority,
      bio: mediaKitData?.intro?.bio?.content
        ? mediaKitData?.intro?.bio?.content?.trim()
        : persistedMediaKitData?.mediaKit?.bio,
      ageRange: mediaKitData?.intro?.ageRange?.age || persistedMediaKitData?.mediaKit?.ageRange,
      posts: persistedMediaKitData?.mediaKit?.posts,
      highestEngagement: persistedMediaKitData?.mediaKit?.highestEngagement,
      highestEngagementPostUrl: persistedMediaKitData?.mediaKit?.highestEngagementPostUrl,
      isFullNameUpdated: mediaKitData?.intro?.profileName?.trim() ? true : false,
      isBioUpdated: mediaKitData?.intro?.bio?.content?.trim() ? true : false,
      isEmailSectionToggle: mediaKitData?.mail?.isVisibility ?? persistedMediaKitData?.mediaKit?.isEmailSectionToggle ?? false,
      isDescriptionSectionToggle:
        mediaKitData?.intro?.bio?.isVisibility ??
        persistedMediaKitData?.mediaKit?.isDescriptionSectionToggle ??
        false,
      isProfilePicUpdated: persistedMediaKitData?.mediaKit?.isProfilePicUpdated,
      isFavFactSectionToggle:
        mediaKitData?.favFacts?.isVisibility ??
        persistedMediaKitData?.mediaKit?.isFavFactSectionToggle ??
        false,
      isAgeRangeUpdated: mediaKitData?.intro?.ageRange?.age ? true : false,
      isDeliverableSectionToggle:
        mediaKitData?.deliverables?.isVisibility ??
        persistedMediaKitData?.mediaKit?.isDeliverableSectionToggle ??
        false,
      isRateSectionToggle:
        mediaKitData?.rates?.isVisibility ??
        persistedMediaKitData?.mediaKit?.isRateSectionToggle ??
        false,
      isCountryCityUpdated:
        mediaKitData?.intro?.userLocation?.location?.city ||
          mediaKitData?.intro?.userLocation?.location?.country
          ? true
          : false,

      country:
        mediaKitData?.intro?.userLocation?.location?.country ||
        persistedMediaKitData?.mediaKit?.country,
      city:
        mediaKitData?.intro?.userLocation?.location?.city || persistedMediaKitData?.mediaKit?.city,
      isCountryIncluded:
        mediaKitData?.intro?.userLocation?.isLocationVisible ??
        persistedMediaKitData?.mediaKit?.isCountryIncluded,
      isAgeRangeIncluded:
        mediaKitData?.intro?.ageRange?.isAgeRangeVisible ??
        persistedMediaKitData?.mediaKit?.isAgeRangeIncluded ??
        false,
      // isPremiumMediaKit: mediaKitData?.isPremiumMediaKit,
      isPremiumMediaKit: true,
      isSampleKit: mediaKitData?.isSavedPassword || persistedMediaKitData?.mediaKit?.isSavedPassword ? false : mediaKitData?.isSampleKit,
      lastUpdatedTheme: currentUser?.currentUser?.currentTheme || 'standard',
      isSavedPassword: mediaKitData?.isSavedPassword ?? persistedMediaKitData?.mediaKit?.isSavedPassword ? true : false,
      isAudienceGenderIncluded: mediaKitData?.audienceGender?.isVisibility ?? persistedMediaKitData?.mediaKit?.isAudienceGenderIncluded ?? false,
      isAudienceLocationIncluded:
        mediaKitData?.audienceLocation?.isVisibility ??
        persistedMediaKitData?.mediaKit?.isAudienceLocationIncluded ??
        false,
      isInstagramAuthenticated: persistedMediaKitData?.mediaKit?.isInstagramAuthenticated,
      isSalesLink:
        mediaKitData?.links?.isVisibility ?? persistedMediaKitData?.mediaKit?.isSalesLink ?? false,
      isCtaBannerIncluded:
        mediaKitData?.cta?.isVisibility ??
        persistedMediaKitData?.mediaKit?.isCtaBannerIncluded ??
        false,
      isBrandPartnerSectionToggle:
        mediaKitData?.brandPartners?.isVisibility ??
        persistedMediaKitData?.mediaKit?.isBrandPartnerSectionToggle ??
        false,
      isSliderVideoSectionToggle:
        mediaKitData?.topVideos?.isVisibility ??
        persistedMediaKitData?.mediaKit?.isSliderVideoSectionToggle ??
        false,
      isReviewSectionToggle:
        mediaKitData?.reviews?.isVisibility ??
        persistedMediaKitData?.mediaKit?.isReviewSectionToggle ??
        false,
      isTopInterestSectionToggle:
        mediaKitData?.topInterest?.isVisibility ??
        persistedMediaKitData?.mediaKit?.isTopInterestSectionToggle ??
        false,
      isTopPostSectionToggle:
        mediaKitData?.topPosts?.isVisibility ??
        persistedMediaKitData?.mediaKit?.isTopPostSectionToggle ??
        false,
      isContentPost1SectionToggle:
        mediaKitData?.contentPost1?.isVisibility ??
        persistedMediaKitData?.mediaKit?.isContentPost1SectionToggle ??
        false,
      isContentPost2SectionToggle:
        mediaKitData?.contentPost2?.isVisibility ??
        persistedMediaKitData?.mediaKit?.isContentPost2SectionToggle ??
        false,
      // Comma-joined legacy draft: each interest must not contain commas (UI strips them in Top Interest editor).
      topInterests:
        mediaKitData?.topInterest?.topInterest?.length > 0
          ? mediaKitData?.topInterest?.topInterest?.join(',')
          : persistedMediaKitData?.mediaKit?.topInterests
            ?.split(',')
            ?.filter(item => item?.trim() !== '')
            ?.join(','),
      isBannerIncluded:
        mediaKitData?.verificationBanner?.isVisibility ??
        persistedMediaKitData?.mediaKit?.isBannerIncluded ??
        false
    },
    socialChannels:
      socialChannelIntro?.length > 0 ? socialChannelIntro : socialChannelPersistedIntro
  };
};

// FAVOURITE FACTS PAYLOAD
export const favFactsDraftPayload = params => {
  const mediaKitData = params?.mediaKitData;
  const persistedMediaKitData = params?.persistedMediaKitData;

  const hasDraftFavFacts = Array.isArray(mediaKitData?.favFacts?.favFacts);

  const favFacts = (mediaKitData?.favFacts?.favFacts || [])
    ?.filter(item => item?.value !== '' && item?.label !== '')
    ?.map(item => {
      return {
        socialChannelName: item?.socialChannelName,
        factName: item?.factName,
        factValue: item?.factValue?.toString(),
        isDeleted_ysn: item?.isDeleted_ysn || false,
        isPaid: item?.isPaid
      };
    });

  const favFactsPersisted = persistedMediaKitData?.favFacts?.map(item => {
    return {
      socialChannelName: item?.socialChannelName,
      factName: item?.factName,
      factValue: item?.factValue,
      isDeleted_ysn: item?.isDeleted_ysn,
      isPaid: item?.isPaid
    };
  });

  return {
    mediaKit: {
      id: persistedMediaKitData?.mediaKit?.id,
      isFavFactsIncluded: true
    },
    mediaKitFavFacts: hasDraftFavFacts ? favFacts : favFactsPersisted
  };
};

// TOP CONTENT VIDEOS PAYLOAD
export const topVidesoContentPayload = params => {
  const mediaKitData = params?.mediaKitData;
  const persistedMediaKitData = params?.persistedMediaKitData;

  const hasDraftTopVideos = Array.isArray(mediaKitData?.topVideos?.topVideos);

  const topVideos = (mediaKitData?.topVideos?.topVideos || []).map(item => {
    return {
      socialChannelName: item?.socialChannelName,
      thumbnailUrl: item?.thumbnailUrl,
      videoUrl: item?.videoUrl
    };
  });

  const topVideosPersisted = persistedMediaKitData?.favouriteContents?.map(item => {
    return {
      socialChannelName: item?.socialChannelName,
      thumbnailUrl: item?.thumbnailUrl,
      videoUrl: item?.videoUrl
    };
  });

  return {
    mediaKit: {
      id: persistedMediaKitData?.mediaKit?.id
    },
    mediaKitFavouriteContents: hasDraftTopVideos ? topVideos : topVideosPersisted
  };
};

export const ctaBannerPayload = params => {
  const mediaKitData = params?.mediaKitData;
  const persistedMediaKitData = params?.persistedMediaKitData;
  const CTA = mediaKitData?.cta;

  return {
    id: persistedMediaKitData?.collaborationBanner?.id,
    mediaKitId: persistedMediaKitData?.mediaKit?.id,
    imageUrl:
      CTA?.ctaBanner?.imageURL ?? persistedMediaKitData?.collaborationBanner?.imageUrl,
    bannerTitle:
      CTA?.ctaBanner?.bannerTitle ?? persistedMediaKitData?.collaborationBanner?.bannerTitle,
    ctaLabel: CTA?.ctaBanner?.ctaLabel ?? persistedMediaKitData?.collaborationBanner?.ctaLabel,
    mobileCtaLabel:
      CTA?.ctaBanner?.mobileCtaLabel ??
      persistedMediaKitData?.collaborationBanner?.mobileCtaLabel ??
      persistedMediaKitData?.collaborationBanner?.ctaLabel,
    contactInfo:
      CTA?.ctaBanner?.contactInfo ?? persistedMediaKitData?.collaborationBanner?.contactInfo,
    isCtaBannerIncluded:
      CTA?.isVisibility ??
      persistedMediaKitData?.mediaKit?.isCtaBannerIncluded ??
      false,
    isEmailContactInfo:
      CTA?.ctaBanner?.isEmailContactInfo ??
      persistedMediaKitData?.collaborationBanner?.isEmailContactInfo
  };
};

// DELIVERABLES PAYLOAD
export const deliverablesPayload = params => {
  const mediaKitData = params?.mediaKitData;
  const persistedMediaKitData = params?.persistedMediaKitData;
  const hasDraftDeliverables = Array.isArray(mediaKitData?.deliverables?.deliverablesData);
  const deliverables = hasDraftDeliverables
    ? mediaKitData?.deliverables?.deliverablesData
    : (persistedMediaKitData?.deliverables || []);
  const mediaKitDeliverables = deliverables
    ?.filter(item => item?.isShow)
    ?.map(item => {
      return {
        deliverableName: item?.deliverableName,
        deliverableDescription: item?.deliverableDescription,
        isShow: item?.isShow,
      };
    });
  return {
    mediaKit: {
      id: persistedMediaKitData?.mediaKit?.id
    },
    mediaKitDeliverables
  };
};

// DELIVERABLES PAYLOAD
export const reviewsPayload = params => {
  const mediaKitData = params?.mediaKitData;
  const persistedMediaKitData = params?.persistedMediaKitData;
  const mediaKitReviews = mediaKitData?.reviews?.list?.map(item => {
    return {
      id: item?.id,
      channel: item?.channel,
      date: item?.date,
      description: item?.description,
      cooperationRating: item?.cooperationRating,
      timelinessRating: item?.timelinessRating,
      contentQualityRating: item?.contentQualityRating,
      profileImage: item?.profileImage,
      isBrand: item?.isBrand,
      brandName: item?.brandName,
      brandUrl: item?.brandUrl,
      completeItem: {
        id: item?.completeItem?.id,
        creatorId: item?.completeItem?.creatorId,
        brandName: item?.completeItem?.brandName,
        brandUrl: item?.completeItem?.brandUrl,
        brandEmail: item?.completeItem?.brandEmail,
        contactName: item?.completeItem?.contactName,
        brandInsta: item?.completeItem?.brandInsta,
        cooperationRating: item?.completeItem?.cooperationRating,
        contentQualityRating: item?.completeItem?.contentQualityRating,
        timelinesRating: item?.completeItem?.timelinesRating,
        review: item?.completeItem?.review,
        collabLink: item?.completeItem?.collabLink,
        isReviewInclude: item?.completeItem?.isReviewInclude,
        isNewReview: item?.completeItem?.isNewReview,
        createdDateTime_dtm: item?.completeItem?.createdDateTime_dtm,
        requestReviewId: item?.completeItem?.requestReviewId,
        mediaKitId: item?.completeItem?.mediaKitId,
        collabLinks: {
          id: item?.completeItem?.collabLinks?.id,
          socialChannelName: item?.completeItem?.collabLinks?.socialChannelName,
          socialPostLink: item?.completeItem?.collabLinks?.socialPostLink,
          mediaUrl: item?.completeItem?.collabLinks?.mediaUrl,
          thumbnailUrl: item?.completeItem?.collabLinks?.thumbnailUrl,
          file: item?.completeItem?.collabLinks?.file,
          requestReviewId: item?.completeItem?.collabLinks?.requestReviewId,
          lastUpdatedDateTime_dtm: item?.completeItem?.collabLinks?.lastUpdatedDateTime_dtm,
          lastUpdatedByUserID_chr: item?.completeItem?.collabLinks?.lastUpdatedByUserID_chr,
          isDeleted_ysn: item?.completeItem?.collabLinks?.isDeleted_ysn,
          row_Version_Binary_bin: item?.completeItem?.collabLinks?.row_Version_Binary_bin,
        },
        lastUpdatedDateTime_dtm: item?.completeItem?.lastUpdatedDateTime_dtm,
        lastUpdatedByUserID_chr: item?.completeItem?.lastUpdatedByUserID_chr,
        isDeleted_ysn: item?.completeItem?.isDeleted_ysn,
        row_Version_Binary_bin: item?.completeItem?.row_Version_Binary_bin
      }
    };
  });

  return {
    mediaKit: {
      id: persistedMediaKitData?.mediaKit?.id
    },
    mediaKitReviews
  };
};

// RATES PAYLOAD
export const ratesPayload = params => {
  const mkData = params?.mediaKitData;
  const persistedMediaKitData = params?.persistedMediaKitData;

  // If the editor has touched rates, mkData.rates.ratesData can be an empty array (user removed all).
  // In that case we must send an explicit empty payload to clear rates on the backend.
  const hasDraftRates = Array.isArray(mkData?.rates?.ratesData);
  const rates = hasDraftRates ? mkData?.rates?.ratesData : (persistedMediaKitData?.rates || []);

  const mediaKitRates = (rates || []).map(item => {
    return {
      socialChannelName: item?.socialChannelName?.trim(),
      minRate: item?.minRate && item?.minRate?.toString()?.trim(),
      maxRate: item?.maxRate && item?.maxRate?.toString()?.trim(),
      isGifted: item?.isGifted ?? false,
      isIncluded: item?.isIncluded ?? false
    };
  });

  return {
    mediaKit: {
      id: persistedMediaKitData?.mediaKit?.id
    },
    mediaKitRates
  };
};

// LINKS PAYLOAD
export const linksPayload = params => {
  const mkData = params?.mediaKitData;
  const persistedMediaKitData = params?.persistedMediaKitData;


  const salesLinks = mkData?.links?.length > 0 ? mkData?.links : persistedMediaKitData?.salesLink;

  const mediaKitSalesLink = salesLinks?.filter(item => item?.isIncluded)?.map((item) => {
    return {
      mediaKitId: persistedMediaKitData?.mediaKit?.id,
      channelName: item?.channelName?.trim(),
      link: item?.link?.trim(),
      isIncluded: item?.isIncluded ?? false
    };
  });


  return {
    mediaKit: {
      id: persistedMediaKitData?.mediaKit?.id
    },
    mediaKitSalesLink
  };
};

// BRAND PARTNERS PAYLOAD — only send absolute logo URLs to backend (never local/bundled paths).
export const brandPartnersPayload = params => {
  const mediaKitData = params?.mediaKitData;
  const persistedMediaKitData = params?.persistedMediaKitData;
  const hasDraftBrandPartners = Array.isArray(mediaKitData?.brandPartners?.list);
  const brandPartners = hasDraftBrandPartners
    ? mediaKitData?.brandPartners?.list
    : (persistedMediaKitData?.brandPartners || []);
  const mediaKitBrandPartners = brandPartners?.map(item => {
    const logoUrl = item?.brandUrl || item?.brandLogoUrl || '';
    const hasAbsoluteLogoUrl =
      logoUrl &&
      (logoUrl.startsWith('http://') || logoUrl.startsWith('https://'));
    if (hasAbsoluteLogoUrl) {
      return {
        brandLogoUrl: logoUrl
      };
    }
    return {
      brandName: item?.brandName
    };
  });

  return {
    mediaKit: {
      id: persistedMediaKitData?.mediaKit?.id
    },
    mediaKitBrandPartners: mediaKitBrandPartners?.length > 0 ? mediaKitBrandPartners : []
  };
};

// SAVE MEDIAKIT LIVE API
export const saveMediaKitLivePayload = params => {
  const mediaKitData = params?.mediaKitData;
  const persistedMediaKitData = params?.persistedMediaKitData;
  const mediaKitId = params?.mediaKitId;

  const mediaKit = saveMediaKitDraftPayload({ mediaKitData, persistedMediaKitData, mediaKitId });
  const faveFacts = favFactsDraftPayload({ mediaKitData, persistedMediaKitData });

  const updatedFavFacts = faveFacts.mediaKitFavFacts?.map(item => ({
    ...item,
    mediaKitID_lng: persistedMediaKitData?.mediaKit?.id
  }));

  const brandPartners = brandPartnersPayload({ mediaKitData, persistedMediaKitData });

  const ctaBanner = ctaBannerPayload({ mediaKitData, persistedMediaKitData });

  const deliverables = deliverablesPayload({ mediaKitData, persistedMediaKitData });

  const reviews = reviewsPayload({ mediaKitData, persistedMediaKitData });

  const rates = ratesPayload({ mediaKitData, persistedMediaKitData });

  const favContent = topVidesoContentPayload({ mediaKitData, persistedMediaKitData });

  const links = linksPayload({ mediaKitData, persistedMediaKitData })

  const payload = {
    instaProfile: null,
    mediaKit: {
      ...mediaKit?.mediaKit,
      isMediaKitLive: true
    },
    mediaKitSocialChannel: mediaKit?.socialChannels,
    mediaKitFavFacts: updatedFavFacts,
    mediaKitData: {
      branParnetes_chr: JSON.stringify(brandPartners),
      collaborationBanner_chr: JSON.stringify(ctaBanner),
      deliverables_chr: JSON.stringify(deliverables),
      favContente_chr: JSON.stringify(favContent),
      mediaKitID_lng: persistedMediaKitData?.mediaKit?.id,
      reviews_chr: JSON.stringify(reviews),
      rates_chr: JSON.stringify(rates),
      salesLink_chr: JSON.stringify(links)
    },
    mediaKitTopPost: persistedMediaKitData?.topPosts
  };
  return payload;
};

// ==================== Audience Data Utilities ====================
// Parse TikTok _chr fields which may be JSON strings or already parsed
export const parseTiktokField = (value) => {
  if (value == null) return value;
  if (typeof value === 'string') {
    try {
      return JSON.parse(value);
    } catch {
      return value;
    }
  }
  return value;
};

export const getAudienceDataByChannel = (audience, selectedChannel) => {
  const instaData = audience?.instagramInsight ?? audience;
  const tiktokData = audience?.tiktokInsight;

  const countriesData = selectedChannel === 'instagram'
    ? (instaData?.audience_country ?? instaData?.audience_Country)
    : (parseTiktokField(tiktokData?.audienceCountry_chr) ?? []);
  const citiesData = selectedChannel === 'instagram'
    ? (instaData?.audience_city ?? instaData?.audience_City)
    : (parseTiktokField(tiktokData?.audienceCity_chr) ?? []);
  const ageRangeData = selectedChannel === 'instagram'
    ? instaData?.audience_range
    : (parseTiktokField(tiktokData?.audienceAge_chr) ?? []);
  const genderData = selectedChannel === 'instagram'
    ? instaData?.audience_gender
    : parseTiktokField(tiktokData?.audienceGender_chr);

  return { countriesData, citiesData, ageRangeData, genderData };
};
